import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { reportError } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/reportService';
import ModalControl, { ModalBody } from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/modalControl';
import { BsDoorClosed } from 'react-icons/bs';
import { BiUser } from 'react-icons/bi';
import { MdFavorite, MdFavoriteBorder, MdOutlineShower, MdOutlineStraighten, MdInfoOutline, MdClose, MdWarningAmber } from 'react-icons/md';
import {
    getWishlistModalDataOnce,
    addWishlist,
    bulkAddWishlist,
    bulkRemoveWishlist,
    removeWishlist,
} from '@PROJECT_ROOT/assets/responsive-frontend/js/apiServices/WishlistService';
import {
    dispatchTrackingEvent,
    buildBaseAnalyticsItem,
    ADD_TO_WISHLIST,
    WISHLIST_ICON_CLICK,
} from '@PROJECT_ROOT/assets/responsive-frontend/js/services/tracking';
import useModalState from '@PROJECT_ROOT/assets/responsive-frontend/js/custom-hooks/useModalState';
import { WISHLIST_CONFIRMATION_MODAL_ID, WISHLIST_MODAL_ID } from '@PROJECT_ROOT/assets/responsive-frontend/js/constants/modalIds';
import { useCurrentUser } from '@PROJECT_ROOT/assets/responsive-frontend/js/contexts/PageContext';

function WishlistToast({ closeToast, toastProps }) {
    return (
        <>
            <MdInfoOutline className={`wishlist-toast__info-icon ${toastProps.data.type === 'success' ? 'wishlist-toast__info-icon--success' : 'wishlist-toast__info-icon--fail'}`} />
            <div className="wishlist-toast__text-container">
                <p className="wishlist-toast__text-title">{toastProps.data.title}</p>
                <p className="wishlist-toast__text-subtitle">{toastProps.data.subtitle}</p>
            </div>
            <button type="button" className="wishlist-toast__close-button" onClick={closeToast}>
                <MdClose className="close-button__icon" />
            </button>
        </>
    );
}

// Ako je proslijeđen prop initialWishlistedUnits, tada se implicira unutarnja kontrola wishlistanih jedinica.
// Težiti korištenju unutarnje kontrole kada je to moguće.
function WishlistWidget({
    unitsData,
    initialWishlistedUnits,
    wishlistedUnits,
    outsideUpdateWishlistedUnits,
    className,
    openLoginModal,
    isUserLoggedIn: isUserLoggedInProp, // Koristi se samo ako je komponenta izvan PageContextProvider-a, npr. PillarPageWishlistWidget.
}) {
    const user = useCurrentUser();
    const isUserLoggedIn = isUserLoggedInProp || !!user?.id;
    const createUnitsWishlistData = (newWishlistedUnits) => {
        if (!newWishlistedUnits) return null;
        const createdUnitsWishlistData = {};
        unitsData.forEach((unitData) => {
            createdUnitsWishlistData[unitData.unitId] = {
                isWishlisted: newWishlistedUnits?.includes(unitData.unitId) ?? false,
                isLoading: false,
            };
        });
        return createdUnitsWishlistData;
    };
    const [unitsWishlistData, setUnitsWishlistData] = useState(createUnitsWishlistData(initialWishlistedUnits));
    const [unitsWishlistDataBeforeEdit, setUnitsWishlistDataBeforeEdit] = useState(null);
    const [isUnitsModalDirty, setIsUnitsModalDirty] = useState(false);
    const [isGlobalLoading, setIsGlobalLoading] = useState(false);
    const [isUnitsModalOpen, setIsUnitsModalOpen] = useModalState(`${WISHLIST_MODAL_ID}-${unitsData[0].unitId}`);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useModalState(`${WISHLIST_CONFIRMATION_MODAL_ID}-${unitsData[0].unitId}`);
    const [unitToRemove, setUnitToRemove] = useState(null);
    const [modalData, setModalData] = useState(null);
    const isMultiUnit = unitsData.length > 1;
    const isAnyUnitWishlisted = useMemo(() => unitsWishlistData && Object.values(unitsWishlistData)
        .filter((unit) => unit.isWishlisted)
        .length > 0, [unitsWishlistData]);
    const isAnyUnitLoading = useMemo(() => unitsWishlistData && Object.values(unitsWishlistData)
        .filter((unit) => unit.isLoading)
        .length > 0, [unitsWishlistData]);
    const isMainWishlistButtonDisabled = !unitsWishlistData || (!isMultiUnit && isAnyUnitLoading);
    const areConfirmationModalButtonsDisabled = unitsWishlistData && (unitsWishlistData[unitToRemove?.unitId]?.isLoading ?? false);

    useEffect(() => {
        if (!isUserLoggedIn) return;
        getWishlistModalDataOnce(isMultiUnit).then(setModalData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isUserLoggedIn]);

    useEffect(() => {
        if (!unitsWishlistData && initialWishlistedUnits) {
            setUnitsWishlistData(createUnitsWishlistData(initialWishlistedUnits));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialWishlistedUnits]);

    useEffect(() => {
        if (wishlistedUnits) {
            setUnitsWishlistData(createUnitsWishlistData(wishlistedUnits));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wishlistedUnits]);

    const showWishlistToast = (title, subtitle, type) => toast(WishlistToast, {
        className: 'wishlist-toast',
        position: 'top-center',
        autoClose: 3000,
        closeButton: false,
        hideProgressBar: true,
        data: { title, subtitle, type },
    });

    const handleUnitsModalClose = () => {
        setUnitsWishlistDataBeforeEdit(null);
        setIsUnitsModalDirty(false);
        setIsUnitsModalOpen(false);
    };

    const updateUnitWishlistData = (newData, unitId) => {
        setUnitsWishlistData((prevState) => ({
            ...prevState,
            [unitId]: {
                ...prevState[unitId],
                ...newData,
            },
        }));
    };

    const dispatchAddedToWishlistTrackingEvent = (unit, unitIndex) => {
        const baseAnalyticsItem = buildBaseAnalyticsItem({ unitItem: unit });
        const addToWishlistData = {
            currency: 'EUR',
            // value: unit.priceWithDiscount, // Iako imamo cijenu, ne šaljemo je.
            items: [{
                ...baseAnalyticsItem,
                index: unitIndex ? unitIndex + 1 : 1,
                item_category: unit.type,
                google_business_vertical: 'travel',
                quantity: 1,
            }],
        };
        dispatchTrackingEvent(ADD_TO_WISHLIST, addToWishlistData);
    };

    const handleAddWishlist = (unit, unitIndex) => {
        updateUnitWishlistData({ isLoading: true }, unit.unitId);
        addWishlist(unit.unitId)
            .then((res) => {
                if (res.data.status !== 'success') {
                    reportError(res);
                    return;
                }
                if (initialWishlistedUnits) {
                    updateUnitWishlistData({ isWishlisted: true }, unit.unitId);
                } else {
                    outsideUpdateWishlistedUnits({ unitsToAdd: [unit.unitId] });
                }
                const message = res.data.message.texts[0].text;
                showWishlistToast(message, unit.title, 'success');
                dispatchAddedToWishlistTrackingEvent(unit, unitIndex);
            })
            .catch((err) => {
                reportError({ ...err, details: `isUserLoggedIn: ${isUserLoggedIn}; userId: ${user?.id}` });
            })
            .finally(() => {
                updateUnitWishlistData({ isLoading: false }, unit.unitId);
            });
    };

    const handleRemoveWishlist = () => {
        if (!unitToRemove) return;
        updateUnitWishlistData({ isLoading: true }, unitToRemove.unitId);
        removeWishlist(unitToRemove.unitId)
            .then((res) => {
                if (res.data.status !== 'success') {
                    reportError(res);
                    return;
                }
                if (initialWishlistedUnits) {
                    updateUnitWishlistData({ isWishlisted: false }, unitToRemove.unitId);
                } else {
                    outsideUpdateWishlistedUnits({ unitsToRemove: [unitToRemove.unitId] });
                }
                const message = res.data.message.texts[0].text;
                showWishlistToast(message, unitToRemove.title, 'danger');
            })
            .catch((err) => {
                reportError(err);
            })
            .finally(() => {
                updateUnitWishlistData({ isLoading: false }, unitToRemove.unitId);
                setIsConfirmationModalOpen(false);
                setUnitToRemove(null);
            });
    };

    const handleMainWishlistClick = (e) => {
        e.preventDefault();
        dispatchTrackingEvent(WISHLIST_ICON_CLICK);
        if (!isUserLoggedIn) {
            openLoginModal?.();
            return;
        }
        if (isMultiUnit) {
            setIsUnitsModalOpen(true);
            setUnitsWishlistDataBeforeEdit(unitsWishlistData);
            return;
        }
        if (isAnyUnitWishlisted) {
            setIsConfirmationModalOpen(true);
            setUnitToRemove(unitsData[0]);
        } else {
            handleAddWishlist(unitsData[0], 0);
        }
    };

    const handleModalWishlistClick = (unit) => {
        setIsUnitsModalDirty(true);
        const isWishlisted = !unitsWishlistData[unit.unitId].isWishlisted;
        if (initialWishlistedUnits) {
            updateUnitWishlistData({ isWishlisted }, unit.unitId);
        } else {
            outsideUpdateWishlistedUnits({ [isWishlisted ? 'unitsToAdd' : 'unitsToRemove']: [unit.unitId] });
        }
    };

    const getUnitsForBulkEdit = () => {
        const unitsToAdd = [];
        const unitsToRemove = [];
        Object.keys(unitsWishlistData).forEach((id) => {
            const isWishlistedBeforeEdit = unitsWishlistDataBeforeEdit[id]?.isWishlisted;
            const isWishlistedAfterEdit = unitsWishlistData[id]?.isWishlisted;
            if (!isWishlistedBeforeEdit && isWishlistedAfterEdit) {
                const unitIndex = unitsData.findIndex((unit) => unit.unitId === Number(id));
                if (unitIndex < 0) return;
                const unit = { ...unitsData[unitIndex], unitIndex };
                unitsToAdd.push(unit);
            }
            if (isWishlistedBeforeEdit && !isWishlistedAfterEdit) {
                const unitIndex = unitsData.findIndex((unit) => unit.unitId === Number(id));
                if (unitIndex < 0) return;
                const unit = { ...unitsData[unitIndex], unitIndex };
                unitsToRemove.push(unit);
            }
        });
        return { unitsToAdd, unitsToRemove };
    };

    const handleUnitsModalCancel = () => {
        if (initialWishlistedUnits) {
            setUnitsWishlistData(unitsWishlistDataBeforeEdit);
        } else {
            const wishlistUnitsUpdates = {
                unitsToAdd: Object.entries(unitsWishlistDataBeforeEdit).filter(([, unit]) => unit.isWishlisted).map(([unitId]) => Number(unitId)),
                unitsToRemove: unitsData.map((unit) => unit.unitId).filter((unitId) => !unitsWishlistDataBeforeEdit[unitId]?.isWishlisted),
            };
            outsideUpdateWishlistedUnits(wishlistUnitsUpdates);
        }
        handleUnitsModalClose();
    };

    const handleUnitsModalConfirm = async () => {
        setIsGlobalLoading(true);
        if (!isUnitsModalDirty) {
            handleUnitsModalClose();
            setIsGlobalLoading(false);
            return;
        }
        const { unitsToAdd, unitsToRemove } = getUnitsForBulkEdit();
        const requests = [
            unitsToAdd.length ? bulkAddWishlist(unitsToAdd.map((unit) => unit.unitId)).catch((err) => reportError(err)) : null,
            unitsToRemove.length ? bulkRemoveWishlist(unitsToRemove.map((unit) => unit.unitId)).catch((err) => reportError(err)) : null,
        ];
        const [addResponse, removeResponse] = await Promise.allSettled(requests);
        if (addResponse.status === 'fulfilled' && addResponse.value?.data.status === 'success') {
            const res = addResponse.value;
            const message = res.data.message.texts[0].text;
            showWishlistToast(message, unitsToAdd.map((unit) => unit.title).join(', '), 'success');
            unitsToAdd.forEach((addedUnit) => {
                dispatchAddedToWishlistTrackingEvent(addedUnit, addedUnit.unitIndex);
            });
        }
        if (removeResponse.status === 'fulfilled' && removeResponse.value?.data.status === 'success') {
            const res = removeResponse.value;
            const message = res.data.message.texts[0].text;
            showWishlistToast(message, unitsToRemove.map((unit) => unit.title).join(', '), 'danger');
        }
        handleUnitsModalClose();
        setIsGlobalLoading(false);
    };

    const handleConfirmationModalClose = () => {
        setIsConfirmationModalOpen(false);
    };

    return (
        <>
            <button
                type="button"
                onClick={handleMainWishlistClick}
                aria-label="Add to wishlist"
                className={`wishlist-widget__button ${isAnyUnitWishlisted ? 'wishlist-widget__button--filled' : ''} ${className ?? ''}`}
                disabled={isGlobalLoading || isMainWishlistButtonDisabled}
            >
                {isAnyUnitWishlisted ? (
                    <MdFavorite size="20" className="wishlist-widget__button-icon" />
                ) : (
                    <MdFavoriteBorder size="20" className="wishlist-widget__button-icon" />
                )}
            </button>
            {modalData && isUnitsModalOpen && (
                <ModalControl
                    id={`${WISHLIST_MODAL_ID}-${unitsData[0].unitId}`}
                    className="wishlist-modal"
                    title={modalData.headerText}
                    isOpen={isUnitsModalOpen}
                    onClose={handleUnitsModalCancel}
                    animate={true}
                    disableBodyScroll={false}
                    leftFooterButton={{
                        content: modalData.cancelText,
                        onClick: handleUnitsModalCancel,
                        disabled: isGlobalLoading,
                    }}
                    rightFooterButton={{
                        content: modalData.confirmText,
                        onClick: handleUnitsModalConfirm,
                        disabled: isGlobalLoading,
                    }}
                >
                    <ModalBody>
                        <p className="wishlist-modal__title">{modalData.titleText}</p>
                        <p className="wishlist-modal__subtitle">{modalData.subtitleText}</p>
                        <div className="wishlist-modal__units">
                            {unitsData.map((unit) => (
                                <div key={unit.unitId} className="wishlist-modal__unit">
                                    <img className="wishlist-modal__unit-image" src={unit.gallery.items[0].image.sourceURL} alt={unit.gallery.items[0].image.altText} />
                                    <div className="wishlist-modal__unit-details">
                                        <p className="wishlist-modal__unit-title">
                                            <span className="text-capitalize">{unit.type.toUpperCase()} </span>
                                            {unit.title}
                                        </p>
                                        <span className="wishlist-modal__unit-numbers">
                                            <span className="wishlist-modal__unit-numbers-item">
                                                <MdOutlineStraighten size="18" className="wishlist-modal__unit-numbers-icon" />
                                                <span className="wishlist-modal__unit-numbers-text" dangerouslySetInnerHTML={{ __html: unit.space }} />
                                            </span>
                                            <span className="wishlist-modal__unit-dot" />
                                            <span className="wishlist-modal__unit-numbers-item">
                                                <BsDoorClosed size="18" className="wishlist-modal__unit-numbers-icon" />
                                                <span className="wishlist-modal__unit-numbers-text">{unit.rooms}</span>
                                            </span>
                                            <span className="wishlist-modal__unit-dot" />
                                            <span className="wishlist-modal__unit-numbers-item">
                                                <MdOutlineShower size="18" className="wishlist-modal__unit-numbers-icon" />
                                                <span className="wishlist-modal__unit-numbers-text">{unit.bathrooms}</span>
                                            </span>
                                            <span className="wishlist-modal__unit-dot" />
                                            <span className="wishlist-modal__unit-numbers-item">
                                                <BiUser size="18" className="wishlist-modal__unit-numbers-icon" />
                                                <span className="wishlist-modal__unit-numbers-text">{unit.people}</span>
                                            </span>
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        className="wishlist-modal__unit-wishlist-button"
                                        onClick={() => handleModalWishlistClick(unit)}
                                        disabled={isGlobalLoading || unitsWishlistData[unit.unitId].isLoading}
                                    >
                                        {unitsWishlistData[unit.unitId].isWishlisted ? (
                                            <MdFavorite className="unit-wishlist-button__icon unit-wishlist-button__icon--orange" />
                                        ) : (
                                            <MdFavoriteBorder className="unit-wishlist-button__icon unit-wishlist-button__icon--black" />
                                        )}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </ModalBody>
                </ModalControl>
            )}
            {modalData && isConfirmationModalOpen && unitToRemove && (
                <ModalControl
                    id={`${WISHLIST_CONFIRMATION_MODAL_ID}-${unitsData[0].unitId}`}
                    className="wishlist-confirmation-modal"
                    isOpen={isConfirmationModalOpen}
                    onClose={handleConfirmationModalClose}
                    animate={true}
                    disableBodyScroll={false}
                    desktopWidth="420px"
                    desktopHeight="auto"
                    mobileHeight="auto"
                >
                    <button type="button" className="modal-control-header__close-button" onClick={handleConfirmationModalClose}>
                        <MdClose className="modal-control-header__close-button-icon" />
                    </button>
                    <MdWarningAmber className="wishlist-confirmation-modal__warning-icon" />
                    <div>
                        <p className="wishlist-confirmation-modal__title">
                            {modalData.removeConfirmationText.replace(/\{\{\s*UNIT_SHORT_TITLE\s*\}\}|UNIT_SHORT_TITLE/g, unitToRemove.title)}
                        </p>
                    </div>
                    <div className="wishlist-confirmation-modal__buttons-container">
                        <button
                            type="button"
                            className="adr-btn-secondary-md w-100"
                            onClick={handleConfirmationModalClose}
                            disabled={isGlobalLoading || areConfirmationModalButtonsDisabled}
                        >
                            {modalData.cancelText ?? modalData.removeConfirmationCancelText}
                        </button>
                        <button
                            type="button"
                            className="adr-btn-primary-md w-100"
                            onClick={handleRemoveWishlist}
                            disabled={isGlobalLoading || areConfirmationModalButtonsDisabled}
                        >
                            {modalData.confirmText ?? modalData.removeConfirmationContinueText}
                        </button>
                    </div>
                </ModalControl>
            )}
        </>
    );
}

export default WishlistWidget;
